import { useId, useMemo } from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  Filler,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { theme } from '../../tailwind.config';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export const SPIDER_CHART_SCALE_MAXIMUM = 100;

const FILL_OPACITY = '33';
const POINT_RADIUS = 3;
const POINT_HOVER_RADIUS = 5;
const BORDER_WIDTH = 2;
const TICK_STEP = 25;
const AXIS_LABEL_FONT_SIZE = 11;
const TICK_FONT_SIZE = 10;
const NO_DATA_LABEL = 'No data';

export type SpiderChartAxis = {
  key: string;
  label: string;
};

export type SpiderChartSeries = {
  key: string;
  label: string;
  /** Any CSS colour. Callers should take these from the Tailwind theme. */
  color: string;
  /**
   * Normalised 0-100 value per axis key. `null` means the value is genuinely
   * unknown for this series, which the chart leaves as a gap rather than
   * plotting as a zero, because a zero reads as "worst in class" and would
   * misrepresent the subject.
   */
  values: Record<string, number | null>;
  /**
   * Display strings for the underlying numbers. When present the tooltip shows
   * these instead of the normalised value, which is a drawing coordinate and
   * means nothing to the reader on its own.
   */
  rawValues?: Record<string, string | null>;
};

export type SpiderChartProps = {
  axes: SpiderChartAxis[];
  series: SpiderChartSeries[];
  /**
   * What the 0-100 scale means, e.g. "percentile among midfielders". Shown
   * under the chart and used in the screen-reader table caption.
   */
  scaleDescription?: string;
  showLegend?: boolean;
  /**
   * Whether the rings carry 25/50/75/100 labels. Turn off when the scale is
   * relative to the subjects on the chart, where a number would imply a
   * percentage of something the reader cannot name.
   */
  showScaleTicks?: boolean;
  /**
   * The outer ring. Defaults to 100 for normalised data; pass a real unit's
   * maximum (with a matching `tickStep`) when every axis shares one unit.
   */
  scaleMaximum?: number;
  tickStep?: number;
  /**
   * Applied to the canvas wrapper, not the whole component. chart.js sizes
   * itself to its parent, so this must carry an explicit height.
   */
  chartClassName?: string;
  className?: string;
};

/**
 * Adds an alpha channel to a six-digit hex colour. Anything else (rgb(), a
 * named colour, an already-transparent hex) is returned untouched so the
 * caller can still pass it, it just will not be softened for the fill.
 */
const withFillOpacity = (color: string): string =>
  /^#[0-9a-f]{6}$/i.test(color) ? `${color}${FILL_OPACITY}` : color;

const formatValue = (value: number | null): string =>
  value === null ? NO_DATA_LABEL : Math.round(value).toString();

/**
 * A radar ("spider") chart for comparing a small number of subjects across a
 * shared set of axes. It is deliberately generic: it knows nothing about
 * players, teams or stats, and expects every value to arrive already
 * normalised onto a common 0-100 scale, because a radar whose axes carry
 * different units produces a shape that means nothing.
 */
const SpiderChart = ({
  axes,
  series,
  scaleDescription,
  showLegend = true,
  showScaleTicks = true,
  scaleMaximum = SPIDER_CHART_SCALE_MAXIMUM,
  tickStep = TICK_STEP,
  chartClassName = 'h-[320px]',
  className
}: SpiderChartProps) => {
  const tableId = useId();

  const data: ChartData<'radar'> = useMemo(
    () => ({
      labels: axes.map((axis) => axis.label),
      datasets: series.map((entry) => ({
        label: entry.label,
        data: axes.map((axis) => entry.values[axis.key] ?? null),
        borderColor: entry.color,
        backgroundColor: withFillOpacity(entry.color),
        pointBackgroundColor: entry.color,
        pointBorderColor: theme.colors.white,
        pointHoverBackgroundColor: entry.color,
        pointRadius: POINT_RADIUS,
        pointHoverRadius: POINT_HOVER_RADIUS,
        borderWidth: BORDER_WIDTH,
        fill: true
      }))
    }),
    [axes, series]
  );

  const options: ChartOptions<'radar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Without this a long axis label on the outer edge is clipped by the
      // canvas rather than shrinking the plotted area to fit.
      layout: { padding: 4 },
      scales: {
        r: {
          min: 0,
          max: scaleMaximum,
          beginAtZero: true,
          angleLines: { color: theme.colors.neutral[200] },
          grid: { color: theme.colors.neutral[200] },
          pointLabels: {
            color: theme.colors.neutral[700],
            font: { size: AXIS_LABEL_FONT_SIZE, family: 'Open Sans' }
          },
          ticks: {
            display: showScaleTicks,
            stepSize: tickStep,
            showLabelBackdrop: false,
            color: theme.colors.neutral[500],
            font: { size: TICK_FONT_SIZE, family: 'Open Sans' }
          }
        }
      },
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            color: theme.colors.neutral[700],
            font: { size: AXIS_LABEL_FONT_SIZE, family: 'Open Sans' }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const entry = series[context.datasetIndex];
              const axis = axes[context.dataIndex];
              const raw = entry?.rawValues?.[axis?.key ?? ''];
              const shown =
                raw ??
                formatValue(
                  context.parsed.r === null || context.parsed.r === undefined
                    ? null
                    : context.parsed.r
                );
              return `${entry?.label ?? ''}: ${shown}`;
            }
          }
        }
      }
    }),
    [axes, series, showLegend, showScaleTicks, scaleMaximum, tickStep]
  );

  if (axes.length === 0 || series.length === 0) return null;

  return (
    <div className={className}>
      <div className={chartClassName}>
        <Radar
          data={data}
          options={options}
          aria-describedby={tableId}
          role="img"
        />
      </div>
      {scaleDescription && (
        <p className="mt-2 mb-0 text-center text-xs text-neutral-600">
          {scaleDescription}
        </p>
      )}
      {/* Canvas is opaque to screen readers, so the same numbers are repeated
          as a table that only assistive technology reaches. */}
      <table id={tableId} className="sr-only">
        <caption>
          {scaleDescription ??
            `Comparison across each measure, scored 0 to ${scaleMaximum}`}
        </caption>
        <thead>
          <tr>
            <th scope="col">Measure</th>
            {series.map((entry) => (
              <th key={entry.key} scope="col">
                {entry.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {axes.map((axis) => (
            <tr key={axis.key}>
              <th scope="row">{axis.label}</th>
              {series.map((entry) => (
                <td key={entry.key}>
                  {entry.rawValues?.[axis.key] ??
                    formatValue(entry.values[axis.key] ?? null)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SpiderChart;
